// -----------------------------------------------------------------------------
// Session continuity.
//
// Tokens expire. Two things follow, and both have to be handled in one place
// rather than at every call site:
//
//   1. The server hands back a fresh token on X-Session-Token once the current
//      one is past halfway through its life. Capturing it is what makes the
//      expiry an idle timeout instead of a hard cut-off mid-task.
//   2. When a session does lapse, every request starts failing. Without this,
//      the user sees a wall of unrelated "the server rejected..." errors and
//      has to work out for themselves that they need to sign in again.
//
// Implemented by wrapping window.fetch once, deliberately. The alternative is
// a helper that every one of the app's several dozen fetch calls has to
// remember to use -- and the one that forgets is the one that silently drops a
// renewed token or swallows an expiry.
// -----------------------------------------------------------------------------

const TOKEN_KEY = "token";
const HEADER = "x-session-token";

type Listener = (token: string) => void;

const renewed = new Set<Listener>();
const expired = new Set<() => void>();

let installed = false;

/** Called with the new token whenever the server rotates it. */
export function onSessionRenewed(fn: Listener): () => void {
  renewed.add(fn);
  return () => renewed.delete(fn);
}

/** Called once when the session has lapsed and the user must sign in again. */
export function onSessionExpired(fn: () => void): () => void {
  expired.add(fn);
  return () => expired.delete(fn);
}

export function installSessionInterceptor(): void {
  if (installed) return;
  installed = true;

  const original = window.fetch.bind(window);
  let announcedExpiry = false;

  window.fetch = async (...args: Parameters<typeof fetch>) => {
    const res = await original(...args);

    try {
      const fresh = res.headers.get(HEADER);
      if (fresh) {
        localStorage.setItem(TOKEN_KEY, fresh);
        announcedExpiry = false;
        renewed.forEach((fn) => fn(fresh));
      }

      if (res.status === 401) {
        // Read a clone: consuming the body here would leave the caller with an
        // already-read response, which is a far more confusing failure than
        // the one being handled.
        const body = await res.clone().json().catch(() => null);
        if (body?.expired && !announcedExpiry) {
          announcedExpiry = true;
          localStorage.removeItem(TOKEN_KEY);
          expired.forEach((fn) => fn());
        }
      }
    } catch {
      // Blocked storage, an opaque response, anything else: never let session
      // bookkeeping break the request it was observing.
    }

    return res;
  };
}
